/** API `globalFilter` body shape for order-getOrdersV2 list requests. */
export interface OrdersGlobalFilter {
  status?: string[];
  /** v4-only: shipment-level status/milestone, separate from the order-level `status`. */
  shipmentStatus?: string[];
  platform?: string[];
  orderIds?: string[];
  waybills?: string[];
  breaze_manifests?: string[];
  customer?: string;
  dateFrom?: string;
  dateTo?: string;
}

/** Scalar or comma-separated / multi-select values before normalization. */
export type OrdersFilterMultiValue = string | string[];

/** Builds the `globalFilter` body for order-getOrdersV2 list requests. */
export interface OrdersGlobalFilterInput {
  status?: OrdersFilterMultiValue;
  shipmentStatus?: OrdersFilterMultiValue;
  platform?: OrdersFilterMultiValue;
  orderIds?: OrdersFilterMultiValue;
  waybills?: OrdersFilterMultiValue;
  manifests?: OrdersFilterMultiValue;
  customer?: string;
  dateFrom?: string;
  dateTo?: string;
}

export function normalizeMultiValues(values: string | string[] | undefined | null): string[] {
  const rawArr = Array.isArray(values)
    ? values
    : typeof values === "string"
      ? values.split(",")
      : values == null
        ? []
        : [values];
  return [...new Set(rawArr.map((v) => String(v).trim()).filter(Boolean))];
}

function hasSpecificMultiValues(values: string[]): boolean {
  return values.some((value) => value && value !== "All");
}

export function toIsoDateBoundary(value: string, boundary: "start" | "end"): string {
  const isDateTimeValue = value.includes("T");
  if (isDateTimeValue) {
    return new Date(value).toISOString();
  }
  const suffix = boundary === "start" ? "T00:00:00" : "T23:59:59.999";
  return new Date(`${value}${suffix}`).toISOString();
}

/** Millisecond timestamps for APIs that expect fromDate/toDate numbers (e.g. transactions). */
export function toMsDateBoundary(value: string, boundary: "start" | "end"): number {
  return new Date(toIsoDateBoundary(value.trim(), boundary)).getTime();
}

export const V4_ONLY_ORDER_STATUSES = new Set(["Partially Fulfilled", "Fulfilled"]);
// OrderV4.status enum — the only values order-getOrdersV4 can filter on. Includes
// Breaze Eligible/Ineligible: v4 orders resolve their "Created" status to one of
// these two once eligibility is determined, so they're valid v4 filter values too
// (not v4-only — v3 orders carry them as well, hence not in V4_ONLY_ORDER_STATUSES).
const V4_ORDER_STATUSES = new Set([
  "Created",
  "Cancelled",
  "Breaze Eligible",
  "Breaze Ineligible",
  ...V4_ONLY_ORDER_STATUSES,
]);

/** Per-schema status values for the dual v3/v4 fetch; null when no specific filter is set. */
export function splitOrdersStatusFilter(
  status: OrdersFilterMultiValue | undefined
): { v3: string[]; v4: string[] } | null {
  const values = normalizeMultiValues(status).filter((value) => value !== "All");
  if (values.length === 0) return null;
  return {
    v3: values.filter((value) => !V4_ONLY_ORDER_STATUSES.has(value)),
    v4: values.filter((value) => V4_ORDER_STATUSES.has(value)),
  };
}

export function buildOrdersGlobalFilter(input: OrdersGlobalFilterInput): OrdersGlobalFilter {
  const filter: OrdersGlobalFilter = {};

  const status = normalizeMultiValues(input.status);
  if (hasSpecificMultiValues(status)) {
    filter.status = status;
  }

  const shipmentStatus = normalizeMultiValues(input.shipmentStatus);
  if (hasSpecificMultiValues(shipmentStatus)) {
    filter.shipmentStatus = shipmentStatus;
  }

  const platform = normalizeMultiValues(input.platform);
  if (hasSpecificMultiValues(platform)) {
    filter.platform = platform;
  }

  if (input.dateFrom?.trim()) {
    filter.dateFrom = toIsoDateBoundary(input.dateFrom.trim(), "start");
  }
  if (input.dateTo?.trim()) {
    filter.dateTo = toIsoDateBoundary(input.dateTo.trim(), "end");
  }

  const orderIds = normalizeMultiValues(input.orderIds);
  if (orderIds.length > 0) {
    filter.orderIds = orderIds;
  }

  const waybills = normalizeMultiValues(input.waybills);
  if (waybills.length > 0) {
    filter.waybills = waybills;
  }

  const manifestIds = normalizeMultiValues(input.manifests);
  if (manifestIds.length > 0) {
    filter.breaze_manifests = manifestIds;
  }

  const customer = input.customer?.trim();
  if (customer) {
    filter.customer = customer;
  }

  return filter;
}
